// ServerDiagnostics — Admin-only server health panel (memory/uptime/crashes,
// memory-trend sparkline, data records, audit trail) + log download / dedup /
// backup / screenshot actions.
// 2026-05-29 EXTRACTED verbatim from src/App.jsx (C1 monolith split, Phase 2d3b2).
// Behavior-preserving move: body identical to the in-App.jsx version; only imports
// were added (it previously closed over App.jsx-scope symbols). Rendered by AdminPanel.
import { useState, useEffect } from 'react';
import { API_BASE } from '../lib/endpoints.js';
import { authHeaders } from '../lib/auth-headers.js';
import { resetSessionTokenCache } from '../utils/session.js';
import { todayGMT2 } from '../utils.js';

function ServerDiagnostics() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingLogs, setDownloadingLogs] = useState(false);

  const fetchDiag = async () => {
    setLoading(true); setError(null);
    try {
      resetSessionTokenCache(); // v28.10 FIX: fresh token for diagnostics
      const res = await fetch(`${API_BASE}/admin/diagnostics`, { headers: authHeaders(), signal: AbortSignal.timeout(8000) });
      if (!res.ok) throw new Error(`${res.status}`);
      setDiag(await res.json());
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const downloadLogs = async () => {
    if (downloadingLogs) return;
    setDownloadingLogs(true);
    try {
      resetSessionTokenCache(); // v28.10 FIX: fresh token for logs download
      const res = await fetch(`${API_BASE}/admin/logs/download`, { headers: authHeaders(), signal: AbortSignal.timeout(15000) });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `blitz-diagnostics-${todayGMT2()}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download failed: ' + e.message);
      setError('Download failed: ' + e.message);
    } finally {
      setDownloadingLogs(false);
    }
  };

  // FIX: was polling every 30s always — now pauses when document is hidden (tab in background)
  useEffect(() => {
    fetchDiag();
    const iv = setInterval(() => { if (!document.hidden) fetchDiag(); }, 30000);
    return () => clearInterval(iv);
  }, []);

  const cardStyle = { background: "#FFFFFF", borderRadius: 16, padding: 20, border: "1px solid rgba(99,102,241,0.1)", boxShadow: "0 2px 12px rgba(99,102,241,0.06)" };
  const labelStyle = { fontSize: 11, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 };
  const valStyle = { fontSize: 20, fontWeight: 700, color: "#0F172A", fontFamily: "'JetBrains Mono',monospace" };

  if (loading && !diag) return <div style={{ textAlign: "center", padding: 40, color: "#64748B" }}>Loading diagnostics...</div>;
  if (error && !diag) return <div style={{ textAlign: "center", padding: 40, color: "#EF4444" }}>Error: {error}</div>;
  if (!diag) return null;

  const mem = diag.memory || {};
  const isHighMem = mem.heapUsed > 300;
  const history = diag.memoryHistory || [];

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Server Diagnostics</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={fetchDiag} style={{ padding: "6px 14px", borderRadius: 8, background: "#F1F5F9", border: "1px solid #E2E8F0", color: "#475569", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Refresh</button>
          <button onClick={downloadLogs} disabled={downloadingLogs} style={{ padding: "6px 14px", borderRadius: 8, background: downloadingLogs ? "#94A3B8" : "linear-gradient(135deg,#0EA5E9,#38BDF8)", border: "none", color: "#FFF", cursor: downloadingLogs ? "default" : "pointer", fontSize: 12, fontWeight: 700, boxShadow: downloadingLogs ? "none" : "0 2px 8px rgba(14,165,233,0.3)" }}>{downloadingLogs ? "Downloading..." : "Download Full Logs"}</button>
          <button onClick={async () => {
            if (!confirm("Remove duplicate CRG deals and Daily Cap entries? (Keeps the record with more data)")) return;
            try {
              const res = await fetch(`${API_BASE}/admin/dedup`, { method: "POST", headers: authHeaders() });
              const data = await res.json();
              if (data.ok) {
                const r = data.results;
                alert(`Dedup complete!\n\nCRG Deals: ${r["crg-deals"]?.removed || 0} duplicates removed\nDaily Cap: ${r["daily-cap"]?.removed || 0} duplicates removed`);
                fetchDiag();
              }
            } catch (e) { alert("Dedup failed: " + e.message); }
          }} style={{ padding: "6px 14px", borderRadius: 8, background: "linear-gradient(135deg,#F59E0B,#FBBF24)", border: "none", color: "#FFF", cursor: "pointer", fontSize: 12, fontWeight: 700, boxShadow: "0 2px 8px rgba(245,158,11,0.3)" }}>🧹 Dedup Data</button>
          <button onClick={async () => {
            try {
              const res = await fetch(`${API_BASE}/admin/backup`, { method: "POST", headers: authHeaders() });
              const data = await res.json();
              if (data.ok) alert(`✅ Backup created: ${data.backup}\n\nUse this before deploying new versions!`);
              else alert("❌ Backup failed: " + (data.error || "Unknown error"));
            } catch (e) { alert("❌ Backup failed: " + e.message); }
          }} style={{ padding: "6px 14px", borderRadius: 8, background: "linear-gradient(135deg,#8B5CF6,#A78BFA)", border: "none", color: "#FFF", cursor: "pointer", fontSize: 12, fontWeight: 700, boxShadow: "0 2px 8px rgba(139,92,246,0.3)" }}>💾 Create Backup</button>
          <button onClick={async () => {
            try {
              const res = await fetch(`${API_BASE}/telegram/screenshot/all`, { method: "POST", headers: authHeaders() });
              const data = await res.json();
              if (data.ok) alert("✅ Both screenshots sent to Telegram!");
              else alert("❌ " + (data.error || "Failed to send screenshots"));
            } catch (e) { alert("❌ " + e.message); }
          }} style={{ padding: "6px 14px", borderRadius: 8, background: "linear-gradient(135deg, #059669 0%, #10B981 50%, #34D399 100%)", border: "none", color: "#FFF", cursor: "pointer", fontSize: 12, fontWeight: 700, boxShadow: "0 2px 8px rgba(16,185,129,0.3)" }}>📸 Send All Screenshots</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
        <div style={cardStyle}><div style={labelStyle}>Version</div><div style={valStyle}>{diag.server?.version || "—"}</div></div>
        <div style={cardStyle}><div style={labelStyle}>Uptime</div><div style={valStyle}>{diag.server?.uptimeFormatted || "—"}</div></div>
        <div style={cardStyle}><div style={labelStyle}>Heap Used</div><div style={{ ...valStyle, color: isHighMem ? "#EF4444" : "#10B981" }}>{mem.heapUsed || 0} MB</div></div>
        <div style={cardStyle}><div style={labelStyle}>RSS Memory</div><div style={valStyle}>{mem.rss || 0} MB</div></div>
        <div style={cardStyle}><div style={labelStyle}>WS Clients</div><div style={valStyle}>{diag.connections?.webSocketClients || 0}</div></div>
        <div style={cardStyle}><div style={labelStyle}>Sessions</div><div style={valStyle}>{diag.connections?.activeSessions || 0}</div></div>
        <div style={cardStyle}><div style={labelStyle}>Crashes</div><div style={{ ...valStyle, color: (diag.server?.crashes || 0) > 0 ? "#EF4444" : "#10B981" }}>{diag.server?.crashes || 0}</div></div>
        <div style={cardStyle}><div style={labelStyle}>TG Errors</div><div style={{ ...valStyle, color: (diag.telegram?.pollingErrors || 0) > 5 ? "#EF4444" : "#10B981" }}>{diag.telegram?.pollingErrors || 0}</div></div>
        <div style={cardStyle}><div style={labelStyle}>Backups</div><div style={{ ...valStyle, color: (diag.backups?.count || 0) > 0 ? "#10B981" : "#EF4444" }}>{diag.backups?.count || 0}</div><div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>{diag.backups?.latest ? `Latest: ${diag.backups.latest.slice(0,19)}` : "No backups!"}</div></div>
      </div>

      {/* v9.06: Crash Log — show recent crashes if any */}
      {diag.server?.recentCrashes && diag.server.recentCrashes.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 16, borderLeft: "3px solid #EF4444" }}>
          <div style={labelStyle}>⚠️ Recent Crashes ({diag.server.recentCrashes.length})</div>
          <div style={{ maxHeight: 150, overflowY: "auto", fontSize: 11, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.6, color: "#DC2626" }}>
            {diag.server.recentCrashes.slice(-5).reverse().map((c, i) => (
              <div key={i} style={{ marginBottom: 4, padding: "4px 8px", background: "rgba(239,68,68,0.05)", borderRadius: 4 }}>
                <span style={{ color: "#94A3B8" }}>{c.time?.slice(11,19) || "?"}</span> [{c.type}] {c.msg}
              </div>
            ))}
          </div>
        </div>
      )}

      {history.length > 1 && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={labelStyle}>Memory Trend (Last {history.length} min)</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 1, height: 60, marginTop: 8 }}>
            {history.map((h, i) => {
              const maxH = Math.max(...history.map(x => x.heapUsed), 100);
              const pct = (h.heapUsed / maxH) * 100;
              return <div key={i} style={{ flex: 1, background: h.heapUsed > 300 ? "#EF4444" : h.heapUsed > 200 ? "#F59E0B" : "#10B981", height: `${pct}%`, borderRadius: 2, minWidth: 2 }} title={`${h.heapUsed}MB at ${h.timestamp?.split('T')[1]?.slice(0,5) || ''}`} />;
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#94A3B8", marginTop: 4 }}>
            <span>{history[0]?.timestamp?.split('T')[1]?.slice(0,5) || ''}</span>
            <span>{history[history.length-1]?.timestamp?.split('T')[1]?.slice(0,5) || ''}</span>
          </div>
        </div>
      )}

      {diag.data && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={labelStyle}>Data Records</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
            {Object.entries(diag.data).map(([k, v]) => (
              <span key={k} style={{ fontSize: 12, color: "#475569" }}><strong>{k}:</strong> {v}</span>
            ))}
          </div>
        </div>
      )}

      {/* v9.06: Audit Trail — "Who changed what and when" */}
      {diag.recentAudit && diag.recentAudit.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={labelStyle}>📋 Recent Activity (Audit Trail)</div>
          <div style={{ maxHeight: 300, overflowY: "auto", marginTop: 8 }}>
            {diag.recentAudit.map((auditFile, fi) => (
              <div key={fi}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6366F1", padding: "6px 0 4px", borderBottom: "1px solid #E2E8F0", marginBottom: 4 }}>{auditFile.file}</div>
                {(auditFile.entries || []).slice(-20).reverse().map((entry, ei) => {
                  if (typeof entry === 'string') return null;
                  const timeStr = entry.timestamp ? entry.timestamp.slice(11, 19) : "?";
                  const actionColor = { login_success: "#10B981", logout: "#94A3B8", update: "#0EA5E9", create: "#8B5CF6", delete: "#EF4444", shutdown: "#F59E0B", blocked_request: "#DC2626", "auto-dedup": "#F59E0B", restore: "#6366F1" }[entry.action] || "#475569";
                  return (
                    <div key={ei} style={{ display: "flex", gap: 8, alignItems: "center", padding: "3px 0", fontSize: 11, borderBottom: "1px solid #F8FAFC" }}>
                      <span style={{ fontFamily: "'JetBrains Mono',monospace", color: "#94A3B8", minWidth: 55 }}>{timeStr}</span>
                      <span style={{ padding: "1px 6px", borderRadius: 4, background: `${actionColor}15`, color: actionColor, fontWeight: 700, fontSize: 10, minWidth: 55, textAlign: "center" }}>{entry.action}</span>
                      <span style={{ color: "#64748B", fontWeight: 600, minWidth: 70 }}>{entry.table}</span>
                      <span style={{ color: "#94A3B8", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.user?.split("@")[0] || "system"} — {(entry.details || "").slice(0, 60)}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { ServerDiagnostics };
export default ServerDiagnostics;
